import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';
import SubPageInnerContentHolder from '../../../components/subPageContentHolder/SubPageInnerContentHolder';
import { HOME_ROUTE } from '../../../lib/constants/route';
import { plusSignSVG, trash2SVG, warningWithCircleSVG } from '../../../lib/allCustomSVGs';
import LoadingSpinner from '../../../ui/loadingSpinner/LoadingSpinner';
import MainSquareButton from '../../../ui/buttons/MainSquareButton';
import ToggleSwitch from '../../../ui/buttons/ToggleSwitch';
import Popup from '../../../ui/popup/Popup';
import CustomSVGs from '../../../ui/customSVGs/CustomSVGs';
import get_action_rules_api_call from '../../../lib/api/actions/get-action-rules';
import create_action_rule_api_call, {
  CreateActionRuleRequest,
} from '../../../lib/api/actions/create-action-rule';
import update_action_rule_api_call from '../../../lib/api/actions/update-action-rule';
import delete_action_rule_api_call from '../../../lib/api/actions/delete-action-rule';
import { ShopActionRule } from '../../../lib/api/actions/action-types';
import { setMessage } from '../../../store/reducers/appSlice';
import '../actions.scss';

const initialFormState = {
  name: '',
  platform: '' as string,
  max_percentage_change: '',
  max_absolute_change: '',
  max_daily_auto_approvals: '10',
  is_active: true,
};

const ActionRules = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch();

  const [rules, setRules] = useState<ShopActionRule[]>([]);
  const [loading, setLoading] = useState(true);
  const [isAddPopupOpen, setIsAddPopupOpen] = useState(false);
  const [isDeletePopupOpen, setIsDeletePopupOpen] = useState(false);
  const [editingRule, setEditingRule] = useState<ShopActionRule | null>(null);
  const [deletingRule, setDeletingRule] = useState<ShopActionRule | null>(null);
  const [isSaving, setIsSaving] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);

  const [formState, setFormState] = useState(initialFormState);

  const loadRules = async () => {
    setLoading(true);
    try {
      const result = await get_action_rules_api_call();
      if (result) {
        setRules(result);
      } else {
        setRules([]);
      }
    } catch (error) {
      console.error('Failed to load action rules:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadRules();
  }, []);

  const openAddPopup = () => {
    setEditingRule(null);
    setFormState(initialFormState);
    setIsAddPopupOpen(true);
  };

  const openEditPopup = (rule: ShopActionRule) => {
    setEditingRule(rule);
    setFormState({
      name: rule.name,
      platform: rule.platform || '',
      max_percentage_change: rule.conditions?.max_percentage_change?.toString() || '',
      max_absolute_change: rule.conditions?.max_absolute_change?.toString() || '',
      max_daily_auto_approvals: rule.max_daily_auto_approvals.toString(),
      is_active: rule.is_active,
    });
    setIsAddPopupOpen(true);
  };

  const closeAddPopup = () => {
    setIsAddPopupOpen(false);
    setEditingRule(null);
    setFormState(initialFormState);
  };

  const openDeletePopup = (rule: ShopActionRule) => {
    setDeletingRule(rule);
    setIsDeletePopupOpen(true);
  };

  const closeDeletePopup = () => {
    setIsDeletePopupOpen(false);
    setDeletingRule(null);
  };

  const handleSaveRule = async () => {
    if (!formState.name.trim()) return;

    setIsSaving(true);
    try {
      const ruleData: CreateActionRuleRequest = {
        name: formState.name.trim(),
        platform: formState.platform || null,
        conditions: {
          max_percentage_change: formState.max_percentage_change
            ? parseFloat(formState.max_percentage_change)
            : undefined,
          max_absolute_change: formState.max_absolute_change
            ? Math.round(parseFloat(formState.max_absolute_change) * 100)
            : undefined,
        },
        max_daily_auto_approvals: parseInt(
          formState.max_daily_auto_approvals || '10',
          10
        ),
        is_active: formState.is_active,
      };

      let result;
      if (editingRule) {
        result = await update_action_rule_api_call(editingRule.id, ruleData);
      } else {
        result = await create_action_rule_api_call(ruleData);
      }

      if (result) {
        dispatch(
          setMessage({
            message: editingRule
              ? t('RuleUpdatedSuccessfully')
              : t('RuleCreatedSuccessfully'),
            messageType: 'success',
          })
        );
        closeAddPopup();
        loadRules();
      } else {
        dispatch(
          setMessage({
            message: t('FailedToSaveRule'),
            messageType: 'error',
          })
        );
      }
    } catch (error) {
      console.error('Failed to save action rule:', error);
      dispatch(
        setMessage({
          message: t('FailedToSaveRule'),
          messageType: 'error',
        })
      );
    } finally {
      setIsSaving(false);
    }
  };

  const handleDeleteRule = async () => {
    if (!deletingRule) return;

    setIsDeleting(true);
    try {
      const success = await delete_action_rule_api_call(deletingRule.id);
      if (success) {
        dispatch(
          setMessage({
            message: t('RuleDeletedSuccessfully'),
            messageType: 'success',
          })
        );
        closeDeletePopup();
        loadRules();
      } else {
        dispatch(
          setMessage({
            message: t('FailedToDeleteRule'),
            messageType: 'error',
          })
        );
      }
    } catch (error) {
      console.error('Failed to delete action rule:', error);
    } finally {
      setIsDeleting(false);
    }
  };

  const handleToggleRuleActive = async (rule: ShopActionRule, active: boolean) => {
    const result = await update_action_rule_api_call(rule.id, {
      is_active: active,
    });
    if (result) {
      setRules((prev) =>
        prev.map((r) => (r.id === rule.id ? { ...r, is_active: active } : r))
      );
    }
  };

  const getPlatformLabel = (platform: string | null): string => {
    if (!platform) return t('AllPlatforms');
    switch (platform) {
      case 'facebook_ads':
        return 'Facebook Ads';
      case 'google_ads':
        return 'Google Ads';
      case 'tiktok_ads':
        return 'TikTok Ads';
      case 'linkedin_ads':
        return 'LinkedIn Ads';
      default:
        return platform;
    }
  };

  return (
    <SubPageInnerContentHolder
      title={t('AutoApprovalRules')}
      backBtnLink={HOME_ROUTE}
      mainBtnTitle={t('AddRule')}
      mainBtnIcon={plusSignSVG}
      mainBtnLoading={false}
      mainBtnAction={openAddPopup}
    >
      {loading ? (
        <div style={{ marginTop: '2rem', textAlign: 'center' }}>
          <LoadingSpinner customLoadingMsg={t('LoadingRules')} />
        </div>
      ) : rules.length === 0 ? (
        <div className='action-empty-state'>
          <p className='action-empty-state__text'>{t('NoRulesConfigured')}</p>
        </div>
      ) : (
        <div className='action-rules-list'>
          {rules.map((rule) => (
            <div key={rule.id} className='action-rule-card'>
              <div
                className='action-rule-card__left'
                onClick={() => openEditPopup(rule)}
                style={{ cursor: 'pointer' }}
              >
                <div className='action-rule-card__left-title'>
                  <h3>{rule.name}</h3>
                  <span className='platform-tag'>
                    {getPlatformLabel(rule.platform)}
                  </span>
                  {!rule.is_active && (
                    <span className='status-badge status-badge--expired'>
                      {t('Inactive')}
                    </span>
                  )}
                </div>
                <div className='action-rule-card__left-details'>
                  {rule.conditions?.max_percentage_change !== undefined && (
                    <span>
                      {t('MaxChange')}: {rule.conditions.max_percentage_change}%
                    </span>
                  )}
                  {rule.conditions?.max_absolute_change !== undefined && (
                    <span>
                      {t('MaxAbsolute')}: $
                      {(rule.conditions.max_absolute_change / 100).toFixed(2)}
                    </span>
                  )}
                  <span>
                    {t('DailyLimit')}: {rule.max_daily_auto_approvals}
                    {rule.daily_auto_approvals_used > 0 &&
                      ` (${rule.daily_auto_approvals_used} ${t('Used')})`}
                  </span>
                </div>
              </div>

              <div className='action-rule-card__right'>
                <div className='action-rule-card__right-toggle'>
                  <ToggleSwitch
                    isOn={rule.is_active}
                    onClick={(checked: boolean) =>
                      handleToggleRuleActive(rule, checked)
                    }
                    id={`rule-toggle-${rule.id}`}
                  />
                </div>
                <div
                  className='action-rule-card__right-delete'
                  onClick={() => openDeletePopup(rule)}
                >
                  <CustomSVGs svg={trash2SVG} />
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Add / Edit Rule Popup */}
      <Popup
        styles={{ backgroundColor: '#fff' }}
        setTrigger={closeAddPopup}
        trigger={isAddPopupOpen}
      >
        <div className='action-rule-form'>
          <h2 className='action-rule-form__title'>
            {editingRule ? t('EditRule') : t('AddNewRule')}
          </h2>

          <div className='action-rule-form__fields'>
            <div className='action-rule-form__field'>
              <label>{t('RuleName')}</label>
              <input
                type='text'
                value={formState.name}
                onChange={(e) =>
                  setFormState((prev) => ({ ...prev, name: e.target.value }))
                }
                placeholder={t('BudgetIncreasesUnder5Percent')}
              />
            </div>

            <div className='action-rule-form__field'>
              <label>{t('Platform')}</label>
              <select
                value={formState.platform}
                onChange={(e) =>
                  setFormState((prev) => ({
                    ...prev,
                    platform: e.target.value,
                  }))
                }
              >
                <option value=''>{t('AllPlatforms')}</option>
                <option value='facebook_ads'>Facebook Ads</option>
                <option value='google_ads'>Google Ads</option>
                <option value='tiktok_ads'>TikTok Ads</option>
                <option value='linkedin_ads'>LinkedIn Ads</option>
              </select>
            </div>

            <div className='action-rule-form__field'>
              <label>{t('MaxPercentageChange')}</label>
              <input
                type='number'
                value={formState.max_percentage_change}
                onChange={(e) =>
                  setFormState((prev) => ({
                    ...prev,
                    max_percentage_change: e.target.value,
                  }))
                }
                placeholder='5'
                min='0'
                step='0.1'
              />
              <p className='action-rule-form__field-description'>
                {t('AutoApproveActionsWithPercentageChangeBelowThisValue')}
              </p>
            </div>

            <div className='action-rule-form__field'>
              <label>{t('MaxAbsoluteChange')}</label>
              <input
                type='number'
                value={formState.max_absolute_change}
                onChange={(e) =>
                  setFormState((prev) => ({
                    ...prev,
                    max_absolute_change: e.target.value,
                  }))
                }
                placeholder='10.00'
                min='0'
                step='0.01'
              />
              <p className='action-rule-form__field-description'>
                {t('AutoApproveActionsWithAbsoluteChangeBelowThisAmount')}
              </p>
            </div>

            <div className='action-rule-form__field'>
              <label>{t('MaxDailyAutoApprovals')}</label>
              <input
                type='number'
                value={formState.max_daily_auto_approvals}
                onChange={(e) =>
                  setFormState((prev) => ({
                    ...prev,
                    max_daily_auto_approvals: e.target.value,
                  }))
                }
                placeholder='10'
                min='1'
                step='1'
              />
              <p className='action-rule-form__field-description'>
                {t('MaximumNumberOfAutoApprovalsPerDayForThisRule')}
              </p>
            </div>

            <div className='action-rule-form__toggle-row'>
              <label>{t('Active')}</label>
              <ToggleSwitch
                isOn={formState.is_active}
                onClick={(checked: boolean) =>
                  setFormState((prev) => ({ ...prev, is_active: checked }))
                }
                id='rule-form-active-toggle'
              />
            </div>
          </div>

          <div className='action-rule-form__buttons'>
            <MainSquareButton
              value={t('Cancel')}
              type='button'
              buttonType='white'
              disabled={isSaving}
              onClick={closeAddPopup}
            />
            <MainSquareButton
              value={editingRule ? t('UpdateRule') : t('CreateRule')}
              type='button'
              disabled={isSaving || !formState.name.trim()}
              loading={isSaving}
              onClick={handleSaveRule}
            />
          </div>
        </div>
      </Popup>

      {/* Delete Confirmation Popup */}
      <Popup
        styles={{ backgroundColor: '#fff' }}
        setTrigger={closeDeletePopup}
        trigger={isDeletePopupOpen}
      >
        <div className='action-delete-confirm'>
          <div className='action-delete-confirm__title'>
            <div className='action-delete-confirm__title-icon'>
              <CustomSVGs svg={warningWithCircleSVG} />
            </div>
            <h3>{t('DeleteRule')}</h3>
          </div>
          <div className='action-delete-confirm__message'>
            <p>{t('AreYouSureYouWantToDeleteThisRule')}</p>
            {deletingRule && (
              <p>
                <strong>{t('Rule')}:</strong> {deletingRule.name}
              </p>
            )}
          </div>
          <div className='action-delete-confirm__buttons'>
            <MainSquareButton
              value={t('Cancel')}
              type='button'
              buttonType='white'
              disabled={isDeleting}
              onClick={closeDeletePopup}
            />
            <MainSquareButton
              value={t('Delete')}
              type='button'
              buttonType='delete'
              disabled={isDeleting}
              loading={isDeleting}
              onClick={handleDeleteRule}
            />
          </div>
        </div>
      </Popup>
    </SubPageInnerContentHolder>
  );
};

export default ActionRules;
